import React from 'react';
import { createDebugLogger } from '../utils/debugLogger';
import { WarningIcon } from '../utils/icons';

const logger = createDebugLogger('[ErrorBoundary]');
import styled from '@emotion/styled';
import { Box, Paper } from '@mui/material';
import { Button } from '@codex/components/inputs';

// ── Styled atoms ──────────────────────────────────────────────────────────────

// Emotion's styled does not have .attrs() — that's a styled-components API.
// Props that need to be on the DOM element go directly in JSX instead.

const Wrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: var(--spacing-6);
  background: var(--color-bg-body);
` as typeof Box;

const Card = styled(Paper)`
  max-width: 480px;
  width: 100%;
  text-align: center;
  border: 1px solid var(--color-border-default) !important;
  border-radius: var(--radius-xl) !important;
  padding: var(--spacing-12) var(--spacing-10);
  background: var(--color-bg-elevated) !important;
` as typeof Paper;

const IconWrapper = styled(Box)`
  font-size: 48px;
  margin-bottom: var(--spacing-4);
  line-height: 1;
`;

const Title = styled('h1')`
  font-family: var(--font-heading);
  font-size: 22px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-3);
`;

const Body = styled('p')`
  font-size: 15px;
  color: var(--color-text-secondary);
  line-height: 1.7;
  margin: 0 0 var(--spacing-8);
`;

const Details = styled('pre')`
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text-disabled);
  background: var(--color-bg-body);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  padding: var(--spacing-4);
  text-align: left;
  overflow: auto;
  max-height: 160px;
  margin: 0 0 var(--spacing-6);
  white-space: pre-wrap;
  word-break: break-word;
`;

const Actions = styled(Box)`
  display: flex;
  gap: var(--spacing-3);
  justify-content: center;
  flex-wrap: wrap;
`;

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  children: React.ReactNode;
  /** Custom fallback to render instead of the default error card */
  fallback?: React.ReactNode;
  /** Called when an error is caught — useful for logging to BetterStack etc. */
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info);

    if (import.meta.env.DEV) {
      logger.error('Caught render error:', error, info.componentStack);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const isDev = import.meta.env.DEV;
    const msg = this.state.error?.message ?? 'An unexpected error occurred.';

    return (
      // component="main": page-level landmark for the error state.
      // role="alert" + aria-live="assertive": announces immediately when the
      // boundary catches, even if focus hasn't moved to this element yet.
      <Wrapper component="main" role="alert" aria-live="assertive">
        <Card elevation={0}>
          {/* aria-hidden="true": emoji is decorative; meaning is in the heading */}
          <IconWrapper aria-hidden="true">
            <WarningIcon sx={{ fontSize: 'inherit' }} />
          </IconWrapper>
          <Title>Something went wrong</Title>
          <Body>
            Vellum hit an unexpected error. You can try recovering without losing your session, or
            reload the page if the problem persists.
          </Body>

          {isDev && this.state.error && <Details title="Error details (dev only)">{msg}</Details>}

          <Actions>
            <Button variant="outlined" size="sm" onClick={this.handleReset}>
              Try to recover
            </Button>
            <Button variant="filled" size="sm" onClick={this.handleReload}>
              Reload page
            </Button>
          </Actions>
        </Card>
      </Wrapper>
    );
  }
}

export default ErrorBoundary;
